<template>
  <v-dialog v-model="isOpen" dark max-width="810px" max-height="400px">
    <v-card>
      <v-card-title>
        <h5>Select Quilt option</h5>
      </v-card-title>
      <v-card-text>
        <section>
          <h6>Quilting Styles</h6>
          <p class="text-body-1">Choose a quilting option for your seat covers' inserts.</p>
          <div class="d-flex flex-wrap justify-start">
            <v-card
            v-for="style in styles"
            :key="style.id"
            width="220"
            :class="{ swatch: true, selectedCard: selectedStyle.id === style.id }"
            style="cursor: pointer"
            @click="setStyle(style)"
            flat
            >
            <v-img
            lazy-src="https://placehold.co/220x220"
            height="220"
            :src="style.image"
            :alt="style.name"
            />
            <v-card-text class="text-center">
              {{style.name}} {{ getDiamondQuiltPrice(style.part)}}
            </v-card-text>
          </v-card>
        </div>
      </section>
      <section v-if="selectedStyle.id">
        <h6>Stitch Colors</h6>
        <p class="text-body-1" v-if="hasThreadColor">Select an insert stitch color. (Not available with diamond quilting.)</p>
        <p class="text-body-1" v-else>Insert stitch color selection not available for diamond quilting.</p>
        <div class="d-flex flex-wrap justify-start" v-if="hasThreadColor">
          <v-card
          v-for="color in colors"
          :key="color.hex"
          width="220"
          class="swatch"
          style="cursor: pointer"
          @click="setQuilt(color)"
          flat
          >
          <v-img
          height="220"
          :src="'https://placehold.co/220x220/'+color.hex+'/'+color.hex"
          :alt="color.name"
          />
          <v-card-text class="text-center">
            {{color.name}} Stitch
          </v-card-text>
        </v-card>
      </div>
    </section>
  </v-card-text>
</v-card>
</v-dialog>
</template>

<style scoped lang="scss">
  .theme--dark.v-card {
    background-color: #353534 !important;
  }
  .v-card__title {
    display: flex;
    align-items: flex-start;
  }
  .flush-left {
    padding-left: 0;
  }
  .swatch {
    margin: 5px 20px 0 0;
  }
  .selectedCard {
    border: 2px solid #ff0000;
  }
  h5 {
    font-size: 20px;
    color: $white;
    text-transform: uppercase;
    font-weight: 700;
    margin: 0;
    font-family: $body-font-family;

    @media screen and (min-width: 600px) {
      font-size: 28px;
    }
  }
  h6 {
    font-size: 22px;
    font-weight: 700;
    color: $white;
    font-family: $body-font-family;
    margin: 0 0 10px;
  }
  p {
    padding-left: 0;
    padding-right: 0;
    word-break: keep-all;

    &.intro-text {
      font-size: 14px;
      line-height: 20px;
    }

    &.sb-text {
      font-size: 14px;
      line-height: 20px;
    }
  }
  .info-panel {
    background-color: transparent !important;

    &::before {
      box-shadow: none !important;
    }
  }
</style>

<script>
  import { mapState, mapGetters } from 'vuex'
  import { getTile, getDescription, quiltStyles } from '@/services/assets'

  export default {
    data() {
      return {
        isOpen: false,
        styles: quiltStyles,
        selectedStyle: quiltStyles[5],
        colors: [
        {
          id: '729c50e0-b89e-47bd-b84c-be2a6bcc0c72',
          name: 'Black',
          hex: '000000',
        },
        {
          id: '3bf9dca5-780f-43cb-b4c1-6e7e095aaa85',
          name: 'White',
          hex: 'ffffff',
        },
        {
          id: 'D',
          name: 'Tan',
          hex: 'B19F82',
        },
        {
          id: '578b30d3-4d3d-4d09-a1e7-c1ae84383b4e',
          name: 'Gray',
          hex: '6A6F84',
        },
        {
          id: 'f875c9ab-d9d8-4892-be3c-53d2bfc021fe',
          name: 'Bright Red',
          hex: 'B90606',
        },
        {
          id: '02367d53-e6b0-4af9-a309-a63d6a59592f',
          name: 'Medium Blue',
          hex: '1829a7',
        },
        {
          id: '0d024de4-193d-4926-9581-95c490d5a34d',
          name: 'Yellow',
          hex: 'f9cc43',
        },
        {
          id: '14902c8f-4172-4e6e-ac5e-f8f26a43886b',
          name: 'Purple',
          hex: '5d319a',
        },
        {
          id: '9bdd6be2-785c-435b-a499-8620e59d1636',
          name: 'Orange',
          hex: 'ff3710',
        },
        {
          id: '999c4305-0c78-4631-a031-5b5a0880d540',
          name: 'Neon Pink',
          hex: 'ff2979',
        },
        {
          id: '2f89cf9f-b9c2-4edb-aea0-f1f2b6a2b7aa',
          name: 'Lime',
          hex: '9bdb4e',
        }
        ]
      }
    },
    computed: {
      ...mapState([
        'pricing',
        'optionPrices',
        ]),
      ...mapGetters([
        'priceOption',
        ]),
      hasThreadColor() {
        const name = this.selectedStyle?.name?.toLowerCase()
        // Only these styles have selectable thread colors
        return ['hex', 'double diamond', 'chain', 'cable'].includes(name)
      },
    },
    methods: {
      getTile: getTile,
      getDescription: getDescription,
      open() {
        this.isOpen = true
      },
      getDiamondQuiltPrice(partNumber) {
        if (!partNumber || !this.optionPrices || !this.pricing) return ''

          const match = Object.values(this.optionPrices)
        .find(option => option.partNumber === partNumber)

      if (!match) return '' // No match found for this quilt partNumber

        const key = this.priceOption(this.pricing)

      // Make sure the field exists (RetailPrice, DealerPrice, etc.)
      const price = match[key] ?? match.RetailPrice ?? 0

      return price ? `($${price})` : ''
    },

    setStyle(style) {
      this.selectedStyle = style

      // Only auto-close if there are truly no thread colors (like Horizontal/None)
      if (!this.hasThreadColor) {
        this.$emit('set-quilt', this.selectedStyle, null)
        this.isOpen = false
      }
    },

    setQuilt(color) {
      if (!this.selectedStyle) {
        return;
      }

      this.$emit('set-quilt', this.selectedStyle, color);

      if (this.selectedStyle && color) {
        this.isOpen = false;
      }
    }

  }
}
</script>
